/* eslint-disable react/jsx-wrap-multilines */
import { GeoapifyContext } from '@geoapify/react-geocoder-autocomplete';
import { enUS, es, fr } from 'date-fns/locale';
import posthog from 'posthog-js';
import { useCallback, useEffect, useRef } from 'react';
import { registerLocale } from 'react-datepicker';
import ReactGA from 'react-ga4';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import './App.css';
import { Login } from './components/Login/Login.component';
import { NoMatch } from './components/NoMatch/NoMatch.component';
import { NoRoutePermission } from './components/NoPermissionAccess/NoRoutePermission';
import {
  AppHeader,
  AppSideBar,
  DashboardLoading,
  Scrollable,
  useGlobalModalContext,
} from './components/_ui';
import NoCompanyAccess from './components/_ui/No-company-access.component';
import { Overview } from './components/overview';
import { Register } from './components/register/Register.component';
import { USER_ROLE, useAuth } from './hooks';
import {
  RoutePath,
  checkIfSupportRoute,
  multiCompanyRoutes,
  routes,
  supportUserMultiCompanyRoutes,
} from './routes';
import { useFetchDeploymentQuery, useFetchEnvironmentQuery } from './services/deployment.api';
import { useFetchReceiptSettingsQuery, useFetchThemeSettingsQuery } from './services/env.api';
import {
  GA_LOCAL_STORAGE_KEY_CLIENT_ID,
  GA_LOCAL_STORAGE_KEY_SESSION_ID,
} from './utils/GoogleAnalytics';

import { SparkResultViewer } from './components/Charger/charger-detail/SparkResultViewer.component';
import { SparkResultViewerV3 } from './components/Charger/charger-detail/SparkResultViewerV3.component';
import ChargerMessageDetaits from './components/Charger/charger-message/ChargerMessageDetails.component';
import { GlobalModal } from './components/_ui/modal';
import CreateCompanySelfServe from './components/onboarding/CreateCompanySelfServe.component';
import { Onboarding } from './components/onboarding/Onboarding.component';
import { ChargerProvider } from './hooks/useChargers';
import { CompanyProvider, useCompany } from './hooks/useCompany';
import { useCompanyPermissions } from './hooks/useCompanyPermissions';
import { AllowedFeaturesProvider } from './hooks/useFeaturePersonalization';
import { LoggerProvider } from './services/logging/logger-context';
import { i18n } from './services/translation/i18n';
import { normalizeLocale } from './utils/locale.Util';

function App() {
  const auth = useAuth();
  const { data: theme } = useFetchThemeSettingsQuery();
  const { data: deploymentData } = useFetchDeploymentQuery();
  const receiptSettingResponse = useFetchReceiptSettingsQuery();
  const { data: env } = useFetchEnvironmentQuery();

  const navRef = useRef(null);
  const contentRef = useRef(null);

  /**
   * Focus handler memoized with useCallback to maintain referential equality between renders.
   * This prevents unnecessary re-renders of the TabNavigationPopup component (React.memo).
   *
   * @param ref - Reference to the DOM element to focus
   */
  const moveFocus = useCallback((ref: any) => {
    if (ref.current) {
      ref.current.focus({ preventScroll: true });

      // Use requestAnimationFrame to ensure focus persists.
      // It checks after the browser's paint cycle and re-apply focus if needed
      requestAnimationFrame(() => {
        if (document.activeElement !== ref.current) {
          ref.current.focus({ preventScroll: true });

          // Add another frame check for extra protection
          requestAnimationFrame(() => {
            if (document.activeElement !== ref.current) {
              ref.current.focus({ preventScroll: true });
            }
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!ReactGA.isInitialized && env?.googleAnalytics.measurementId) {
      ReactGA.initialize(env.googleAnalytics.measurementId);

      // client id
      ReactGA.ga((tracker: any) => {
        tracker.get('clientId');
        localStorage.setItem(GA_LOCAL_STORAGE_KEY_CLIENT_ID, tracker.get('clientId'));
      });

      ReactGA.gtag('get', env.googleAnalytics.measurementId, 'session_id', (id: any) => {
        localStorage.setItem(GA_LOCAL_STORAGE_KEY_SESSION_ID, id);
      });
    }
  }, [env]);

  const userLanguage = auth.user?.attributes?.profile?.sitehostDashboardLanguage;

  useEffect(() => {
    const browserLanguage = navigator.languages?.[0] || navigator.language;
    const lang = normalizeLocale(userLanguage || theme?.default_language || browserLanguage);

    i18n.changeLanguage(lang);
  }, [userLanguage, theme?.default_language]);

  useEffect(() => {
    registerLocale('es', es);
    registerLocale('fr', fr);
    registerLocale('en-US', enUS);
    if (document) {
      document.documentElement.lang = i18n.language;
    }
  }, []);

  useEffect(() => {
    if (deploymentData?.postHogApiKey && deploymentData?.postHogApiHost) {
      posthog.init(deploymentData.postHogApiKey, {
        api_host: deploymentData.postHogApiHost,
        opt_in_site_apps: true,
      });
    }
  }, [deploymentData?.postHogApiKey, deploymentData?.postHogApiHost]);

  useEffect(() => {
    if (receiptSettingResponse.isSuccess) {
      // tab title will be determined based on the domain
      document.title = receiptSettingResponse.data.companyName || '';
      const descMeta = document.getElementsByName('description');
      if (descMeta && descMeta.length > 0) {
        descMeta[0].textContent = receiptSettingResponse.data.companyName || '';
      }
      const touchIcon = document.getElementById('touchicon') as HTMLAnchorElement | null;
      if (touchIcon) {
        touchIcon.href = `/assets?resourceId=favicon&domainName=${window.location.hostname}`;
      }
    }
  }, [receiptSettingResponse]);

  useEffect(() => {
    const element = document.getElementById('favicon') as HTMLAnchorElement | null;
    if (element) {
      element.href = `/assets?resourceId=favicon&domainName=${window.location.hostname}`;
    }
  }, []);

  useEffect(() => {
    if (!theme) return;
    const root = document.documentElement;
    root.style.setProperty('--brand-0', theme.brand_0);
    root.style.setProperty('--brand-1', theme.brand_1);
    root.style.setProperty('--brand-2', theme.brand_2);
    root.style.setProperty('--brand-3', theme.brand_3);
  }, [theme]);

  const Layout = (scrollbarRef: any) => {
    return (
      <>
        <div className='skip-links'>
          <a
            href='#navigation'
            className='skip-link'
            onClick={(e) => {
              e.preventDefault();
              moveFocus(navRef);
            }}
          >
            Skip to side navigation
          </a>
          <a
            href='#main-content'
            className='skip-link'
            onClick={(e) => {
              e.preventDefault();
              moveFocus(contentRef);
            }}
          >
            Skip to page content
          </a>
        </div>
        <div className='flex flex-row flex-nowrap w-full min-h-screen'>
          <nav
            id='navigation'
            tabIndex={-1}
            ref={navRef}
            className='flex flex-none w-0 xl:w-60 invisible xl:visible h-screen sticky top-0 z-10'
          >
            <AppSideBar />
          </nav>
          <Scrollable ref={scrollbarRef}>
            <div className='w-full bg-web_bg min-h-screen'>
              <div className='sticky top-0 z-[100] w-full'>
                <AppHeader />
              </div>
              <div
                id='main-content'
                tabIndex={-1}
                ref={contentRef}
                className='min-w-[1024px] mx-auto p-[24px] m-auto'
              >
                <Outlet />
              </div>
            </div>
          </Scrollable>
        </div>
      </>
    );
  };

  // Function checks user's auth as well as user's company access
  const ProtectedLayout = () => {
    const location = useLocation();
    const { hideModal } = useGlobalModalContext();
    const scrollbarRef = useRef(null);
    const { companyId, companies: companylist, isMultiViewActive } = useCompany();
    const singleCompanyIdAvailable = companyId && companyId.toUpperCase() !== 'ALL';
    const {
      canViewFinances,
      canViewPricing,
      canViewPowerManagement,
      canViewVehicles,
      canViewRfidCards,
      canViewAccessRules,
      isPermissionsLoading,
    } = useCompanyPermissions(singleCompanyIdAvailable ? companyId : undefined);

    useEffect(() => {
      // hide the modal if users click back button on browser
      hideModal();
      // scroll window to 0,0 after path changed
      scrollbarRef?.current && (scrollbarRef.current as any).scrollToTop();

      // Set focus to the main content area after navigation
      // This ensures that when a user navigates to a new page, focus moves to the content
      // rather than staying in the navigation menu
      setTimeout(() => {
        if (contentRef?.current) {
          moveFocus(contentRef);
        }
      }, 100);
    }, [location.pathname, hideModal]);

    // Check user auth permission
    if (!auth.user) {
      return <Navigate to='/login' state={{ from: location }} replace />;
    }

    // Show loading dots until we get company list, and until company-detail permissions load
    // (otherwise route guards see all-false permissions and redirect to /noRoutePermission).
    if (!companylist || (singleCompanyIdAvailable && isPermissionsLoading)) {
      return (
        <div className='h-screen bg-[#E5E5E5]'>
          <DashboardLoading />
        </div>
      );
    }

    // No current access but user has an hsContactId - redirect to new company self-serve flow
    if (
      companylist?.length === 0 &&
      (auth.user?.attributes?.profile?.hsContactId || location.search.includes('hsContactId')) &&
      location.pathname !== `/${RoutePath.CREATE_COMPANY_SELF_SERVE}`
    ) {
      return <Navigate to={`/${RoutePath.CREATE_COMPANY_SELF_SERVE}`} replace />;
    }

    // User is already logged in & directly opens the root page
    if (location.pathname === '/' && !companyId) {
      if (companylist?.length > 0 && auth.user?.attributes?.profile?.lastActiveCompanyId) {
        return (
          <Navigate
            to={`/${RoutePath.OVERVIEW}?companyId=${
              auth.user?.attributes?.profile?.lastActiveCompanyId || ''
            }`}
            replace
          />
        );
      }

      return <Navigate to={`/${RoutePath.NO_ACCESS}`} replace />;
    }

    // Check route permissions before allowing access
    if (location.pathname === `/${RoutePath.POWER}` && !canViewPowerManagement) {
      return <Navigate to={RoutePath.NO_ROUTE_PERMISSION} replace />;
    }
    if (
      (location.pathname === `/${RoutePath.PRICING}` ||
        location.pathname === `/${RoutePath.PRICING_EDIT}`) &&
      !canViewPricing
    ) {
      return <Navigate to={RoutePath.NO_ROUTE_PERMISSION} replace />;
    }
    if (location.pathname === `/${RoutePath.PAYOUT}` && !canViewFinances) {
      return <Navigate to={RoutePath.NO_ROUTE_PERMISSION} replace />;
    }
    const isVehiclesRoute =
      location.pathname === `/${RoutePath.VEHICLES}` ||
      Boolean(location.pathname.match(/^\/vehicles\/([^/]+)$/));
    if (isVehiclesRoute && !canViewVehicles) {
      return <Navigate to={RoutePath.NO_ROUTE_PERMISSION} replace />;
    }
    if (location.pathname === `/${RoutePath.RFID}` && !canViewRfidCards) {
      return <Navigate to={RoutePath.NO_ROUTE_PERMISSION} replace />;
    }
    if (location.pathname === `/${RoutePath.ACCESS}` && !canViewAccessRules) {
      return <Navigate to={RoutePath.NO_ROUTE_PERMISSION} replace />;
    }
    // copyed link is valid.
    if (companyId && companylist?.find((c) => c.id === companyId)) {
      if (companyId !== auth?.user?.attributes?.profile?.lastActiveCompanyId) {
        auth.updateCompanyId(companyId, false);
      }

      return Layout(scrollbarRef);
    }

    // User have no access to any company
    if (
      location.pathname === `/${RoutePath.NO_ACCESS}` ||
      location.pathname === `/${RoutePath.CREATE_COMPANY_SELF_SERVE}`
    ) {
      return Layout(scrollbarRef);
    }

    if (companyId?.toUpperCase() === 'ALL') {
      // For support users and non support users with multicompanyview access should be
      // able to access the allowed routes
      const isPathAllowed = (
        auth.role === USER_ROLE.SUPPORT ? supportUserMultiCompanyRoutes : multiCompanyRoutes
      )?.find(
        (route) =>
          route === location.pathname ||
          // regular expression for '/chargers/:chargerId' match
          location.pathname.match(/^\/chargers\/([^/]+)$/),
      );

      // Redirect to NOT_FOUND if authorization or path is not allowed
      if (!isPathAllowed) {
        return <Navigate to={RoutePath.NOT_FOUND} replace />;
      }
    }

    if (isMultiViewActive) {
      return Layout(scrollbarRef);
    }

    // not allowed to see particular route
    return <Navigate to={RoutePath.NO_ROUTE_PERMISSION} replace />;
  };

  if (!deploymentData || !theme) {
    return null;
  }
  return (
    <LoggerProvider>
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <GeoapifyContext apiKey={deploymentData?.geoapifyApiKey || ''}>
          <CompanyProvider>
            <ChargerProvider>
              <AllowedFeaturesProvider>
                <GlobalModal>
                  <Routes>
                    <Route path='/login' element={<Login />} />
                    <Route path='/register' element={<Register />} />
                    <Route path={RoutePath.ONBOARDING} element={<Onboarding />} />

                    <Route path='/noRoutePermission' element={<NoRoutePermission />} />
                    <Route path={RoutePath.CHARGER_MESSAGE} element={<ChargerMessageDetaits />} />
                    <Route path={RoutePath.SPARK_RESULTS} element={<SparkResultViewer />} />
                    <Route path={RoutePath.SPARK_RESULTS_V3} element={<SparkResultViewerV3 />} />

                    <Route path='/' element={<ProtectedLayout />}>
                      <Route index element={<Overview />} />
                      <Route
                        key={RoutePath.CREATE_COMPANY_SELF_SERVE}
                        path={RoutePath.CREATE_COMPANY_SELF_SERVE}
                        element={<CreateCompanySelfServe />}
                      />

                      <Route
                        key={RoutePath.NO_ACCESS}
                        path={RoutePath.NO_ACCESS}
                        element={<NoCompanyAccess />}
                      />

                      {routes
                        .filter(
                          (route) =>
                            route.path !== RoutePath.NO_ACCESS &&
                            route.path !== RoutePath.NOT_FOUND &&
                            route.path !== RoutePath.CREATE_COMPANY_SELF_SERVE &&
                            checkIfSupportRoute(auth.role, route.role),
                        )
                        .map((route) => (
                          <Route key={route.path} path={route.path} element={<route.component />} />
                        ))}
                    </Route>
                    <Route
                      key={RoutePath.NOT_FOUND}
                      path={RoutePath.NOT_FOUND}
                      element={<NoMatch />}
                    />
                    <Route path='*' element={<NoMatch />} />
                  </Routes>
                </GlobalModal>
              </AllowedFeaturesProvider>
            </ChargerProvider>
          </CompanyProvider>
        </GeoapifyContext>
      </QueryParamProvider>
    </LoggerProvider>
  );
}

export default App;
